/**
 * SuperAdmin Invoices — Subscription invoice list with PDF download.
 * Route: rendered inside SuperAdminDashboard.js (activePage === 'invoices').
 * API: GET /api/superadmin/invoices  (paginated, 20/page)
 *      GET /api/superadmin/invoices/:id/pdf-url (Cloudinary public URL)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiRefreshCw, FiDownload } from 'react-icons/fi';
import api from '../utils/api';

function fmtINR(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

const HEADERS = ['Invoice No','Date','Name','Email','Plan','Base ₹','GST%','GST ₹','CGST ₹','SGST ₹','IGST ₹','Total ₹','City','Payment','Instrument','Status','PDF'];
const RIGHT_ALIGN = new Set(['Base ₹','GST%','GST ₹','CGST ₹','SGST ₹','IGST ₹','Total ₹']);

const S = {
  wrap:     { padding: 0 },
  toolbar:  { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' },
  select:   { padding: '8px 10px', borderRadius: '8px', border: '1px solid #e8eae8', fontSize: '13px', minHeight: '36px', color: '#1a1a2e' },
  btn:      (busy) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: 'none', background: busy ? '#9CA3AF' : '#1a6b5a', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: busy ? 'default' : 'pointer', minHeight: '36px' }),
  scroll:   { overflowX: 'auto' },
  table:    { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e8eae8', minWidth: '1100px' },
  th:       { fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.4px', padding: '10px 12px', background: '#f9fafb', borderBottom: '1px solid #e8eae8', whiteSpace: 'nowrap', textAlign: 'left' },
  thR:      { textAlign: 'right' },
  td:       { fontSize: '12px', color: '#1a1a2e', padding: '10px 12px', borderBottom: '1px solid #f4f6f4', verticalAlign: 'middle', whiteSpace: 'nowrap' },
  tdR:      { textAlign: 'right' },
  badge:    (s) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', background: s === 'paid' ? '#EAF7EF' : '#FDEDEC', color: s === 'paid' ? '#27AE60' : '#E74C3C' }),
  pdfBtn:   { background: 'none', border: '1px solid #1a6b5a', color: '#1a6b5a', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer' },
  pager:    { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', marginTop: '14px', fontSize: '13px', color: '#555' },
  pgBtn:    (dis) => ({ padding: '6px 14px', borderRadius: '8px', border: '1px solid #e8eae8', background: dis ? '#f4f6f4' : '#fff', color: dis ? '#bbb' : '#1a6b5a', cursor: dis ? 'default' : 'pointer', fontWeight: '600', fontSize: '13px' }),
  empty:    { padding: '32px', textAlign: 'center', color: '#888', fontSize: '13px' },
  loading:  { padding: '48px', textAlign: 'center', color: '#888', fontSize: '14px' },
  error:    { padding: '14px', background: '#FDEDEC', borderRadius: '8px', color: '#E74C3C', fontSize: '13px', marginBottom: '16px' },
};

const SuperAdminInvoices = () => {
  // useMemo: date-derived arrays must be inside component to avoid stale values after midnight
  const monthOptions = useMemo(() => Array.from({ length: 12 }, (_, i) => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }), []);
  const quarterOptions = useMemo(() => {
    const opts = []; let y = new Date().getFullYear(), q = Math.ceil((new Date().getMonth() + 1) / 3);
    for (let i = 0; i < 8; i++) { opts.push(`Q${q}-${y}`); q--; if (q < 1) { q = 4; y--; } }
    return opts;
  }, []);

  const [invoices,   setInvoices]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [pdfError,   setPdfError]   = useState(null);
  const [filterType, setFilterType] = useState('month');
  const [selMonth,   setSelMonth]   = useState(() => monthOptions[0]);
  const [selQuarter, setSelQuarter] = useState(() => quarterOptions[0]);
  const [pdfLoading, setPdfLoading] = useState({});
  const [xlsLoading, setXlsLoading] = useState(false);

  const handleExport = async () => {
    if (xlsLoading) return;
    setXlsLoading(true); setPdfError(null);
    try {
      const params = filterType === 'month' ? { month: selMonth } : { quarter: selQuarter };
      const res = await api.get('/superadmin/invoices/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `LifeChiAI-GST-${filterType === 'month' ? selMonth : selQuarter}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setPdfError('XLS export failed. Please try again.');
    } finally { setXlsLoading(false); }
  };

  const load = useCallback(async (pg = 1) => {
    setLoading(true); setError(null); setPdfError(null);
    try {
      const params = { page: pg, ...(filterType === 'month' ? { month: selMonth } : { quarter: selQuarter }) };
      const res = await api.get('/superadmin/invoices', { params });
      const d   = res.data.data;
      setInvoices(d.invoices); setTotal(d.total); setPage(d.page); setTotalPages(d.totalPages);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load invoices');
    } finally { setLoading(false); }
  }, [filterType, selMonth, selQuarter]);

  useEffect(() => { load(1); }, [load]);

  const handlePdf = async (inv) => {
    if (pdfLoading[inv._id] || !inv.pdfCloudinaryId) return;
    setPdfLoading(p => ({ ...p, [inv._id]: true })); setPdfError(null);
    try {
      const res = await api.get(`/superadmin/invoices/${inv._id}/pdf-url`);
      window.open(res.data.data.url, '_blank', 'noopener');
    } catch (e) {
      setPdfError(`PDF unavailable for invoice ${inv.invoiceNumber || inv._id}.`);
    } finally { setPdfLoading(p => ({ ...p, [inv._id]: false })); }
  };

  if (loading && !invoices.length) return <div style={S.loading}>Loading invoices…</div>;

  return (
    <div style={S.wrap}>
      {error    && <div style={S.error}>{error}</div>}
      {pdfError && <div style={S.error}>{pdfError}</div>}

      <div style={S.toolbar}>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={S.select}>
          <option value="month">Month</option>
          <option value="quarter">Quarter</option>
        </select>
        {filterType === 'month'
          ? <select value={selMonth}   onChange={e => setSelMonth(e.target.value)}   style={S.select}>{monthOptions.map(m => <option key={m} value={m}>{m}</option>)}</select>
          : <select value={selQuarter} onChange={e => setSelQuarter(e.target.value)} style={S.select}>{quarterOptions.map(q => <option key={q} value={q}>{q}</option>)}</select>
        }
        <button onClick={() => load(1)} disabled={loading} style={S.btn(loading)} type="button">
          <FiRefreshCw size={13} />{loading ? 'Loading…' : 'Load'}
        </button>
        <button onClick={handleExport} disabled={xlsLoading} style={S.btn(xlsLoading)} type="button">
          <FiDownload size={13} />{xlsLoading ? 'Exporting…' : 'Export XLS'}
        </button>
        <span style={{ fontSize: '12px', color: '#888', marginLeft: 'auto' }}>{total} invoice{total !== 1 ? 's' : ''}</span>
      </div>

      <div style={S.scroll}>
        <table style={S.table}>
          <thead><tr>
            {HEADERS.map(h => <th key={h} style={{ ...S.th, ...(RIGHT_ALIGN.has(h) ? S.thR : {}) }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {!invoices.length
              ? <tr><td colSpan={HEADERS.length} style={S.empty}>No invoices found for this period.</td></tr>
              : invoices.map(inv => (
                <tr key={String(inv._id)}>
                  <td style={S.td}>{inv.invoiceNumber || '—'}</td>
                  <td style={S.td}>{fmtDate(inv.createdAt)}</td>
                  <td style={S.td}>{inv.tenantId?.tenantName || `${inv.userId?.firstName || ''} ${inv.userId?.lastName || ''}`.trim() || '—'}</td>
                  <td style={S.td}>{inv.tenantId?.contactEmail || inv.userId?.email || '—'}</td>
                  <td style={S.td}>{inv.planDisplayName || inv.planName}</td>
                  <td style={{ ...S.td, ...S.tdR }}>{fmtINR((inv.amountInr   || 0) / 100)}</td>
                  <td style={{ ...S.td, ...S.tdR }}>{inv.gstPercent ?? 18}%</td>
                  <td style={{ ...S.td, ...S.tdR }}>{fmtINR((inv.gstAmount   || 0) / 100)}</td>
                  <td style={{ ...S.td, ...S.tdR }}>{fmtINR((inv.cgstAmount  || 0) / 100)}</td>
                  <td style={{ ...S.td, ...S.tdR }}>{fmtINR((inv.sgstAmount  || 0) / 100)}</td>
                  <td style={{ ...S.td, ...S.tdR }}>{fmtINR((inv.igstAmount  || 0) / 100)}</td>
                  <td style={{ ...S.td, ...S.tdR }}>{fmtINR((inv.totalAmount || 0) / 100)}</td>
                  <td style={S.td}>{inv.tenantId?.city || '—'}</td>
                  <td style={S.td}>{inv.autoDebit ? 'Auto-debit' : 'Manual'}</td>
                  <td style={S.td}>{inv.paymentMethod || '—'}</td>
                  <td style={S.td}><span style={S.badge(inv.status)}>{inv.status}</span></td>
                  <td style={S.td}>
                    {inv.pdfCloudinaryId
                      ? <button style={S.pdfBtn} disabled={!!pdfLoading[inv._id]} onClick={() => handlePdf(inv)} type="button">
                          <FiDownload size={11} style={{ marginRight: 3 }} />{pdfLoading[inv._id] ? '…' : 'PDF'}
                        </button>
                      : '—'}
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={S.pager}>
          <button style={S.pgBtn(page <= 1)}          disabled={page <= 1}          onClick={() => load(page - 1)} type="button">← Prev</button>
          <span>Page {page} of {totalPages}</span>
          <button style={S.pgBtn(page >= totalPages)} disabled={page >= totalPages} onClick={() => load(page + 1)} type="button">Next →</button>
        </div>
      )}
    </div>
  );
};

export default SuperAdminInvoices;
